import { useEffect, useState } from "react";
import { clientApi, getApiErrorMessage } from "@/lib/api";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { formatCurrency } from "@/lib/format";
import { toast } from "sonner";
import { Target, Plus, Trash2, CheckCircle2, AlertCircle, GraduationCap, Home, Car, Plane, Heart, Landmark, Sparkles } from "lucide-react";

const GOAL_TYPES = [
  ["RETIREMENT", "Retirement", Landmark],
  ["EDUCATION", "Education", GraduationCap],
  ["HOUSE", "Dream House", Home],
  ["CAR", "Car", Car],
  ["VACATION", "Vacation", Plane],
  ["MARRIAGE", "Marriage", Heart],
  ["WEALTH", "Wealth Creation", Sparkles],
];

function GoalDialog({ onSaved, holdings }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", goal_type: "WEALTH", target_amount: "", target_date: "", expected_return_pct: 12, monthly_investment: "", mapped_scheme_codes: [] });
  const [saving, setSaving] = useState(false);

  const toggleScheme = (code) => setForm((f) => ({
    ...f, mapped_scheme_codes: f.mapped_scheme_codes.includes(code)
      ? f.mapped_scheme_codes.filter((c) => c !== code) : [...f.mapped_scheme_codes, code],
  }));

  const save = async () => {
    setSaving(true);
    try {
      await clientApi.post("/goals", {
        ...form,
        target_amount: Number(form.target_amount),
        monthly_investment: Number(form.monthly_investment) || 0,
        expected_return_pct: Number(form.expected_return_pct) || 12,
      });
      toast.success("Goal created");
      setOpen(false);
      setForm({ name: "", goal_type: "WEALTH", target_amount: "", target_date: "", expected_return_pct: 12, monthly_investment: "", mapped_scheme_codes: [] });
      onSaved();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid="goal-create-button"><Plus className="mr-1 h-4 w-4" /> New Goal</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-[var(--brand)]">Create a goal</DialogTitle>
          <DialogDescription className="text-xs">Set a target and map holdings to track your progress.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {GOAL_TYPES.map(([k, label, Icon]) => (
              <button key={k} onClick={() => setForm((f) => ({ ...f, goal_type: k, name: f.name || label }))}
                className={`flex flex-col items-center gap-1 rounded-xl border p-2 text-[10px] ${form.goal_type === k ? "border-[var(--cta)] bg-[var(--surface-2)]" : "border-[var(--glass-border)]"}`}
                data-testid={`goal-type-${k.toLowerCase()}`}>
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>
          <Input placeholder="Goal name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="goal-name-input" />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div className="space-y-1"><Label className="text-xs">Target amount (₹)</Label>
              <Input type="number" value={form.target_amount} onChange={(e) => setForm((f) => ({ ...f, target_amount: e.target.value }))} data-testid="goal-target-input" /></div>
            <div className="space-y-1"><Label className="text-xs">Target month</Label>
              <Input type="month" value={form.target_date} onChange={(e) => setForm((f) => ({ ...f, target_date: e.target.value }))} data-testid="goal-date-input" /></div>
            <div className="space-y-1"><Label className="text-xs">Expected return % p.a.</Label>
              <Input type="number" value={form.expected_return_pct} onChange={(e) => setForm((f) => ({ ...f, expected_return_pct: e.target.value }))} data-testid="goal-return-input" /></div>
            <div className="space-y-1"><Label className="text-xs">Monthly investment (₹)</Label>
              <Input type="number" value={form.monthly_investment} onChange={(e) => setForm((f) => ({ ...f, monthly_investment: e.target.value }))} data-testid="goal-monthly-input" /></div>
          </div>
          {holdings.length > 0 && (
            <div className="space-y-1">
              <Label className="text-xs">Map existing holdings (their value counts toward this goal)</Label>
              <div className="max-h-32 space-y-1 overflow-y-auto rounded-xl border border-[var(--glass-border)] p-2">
                {holdings.map((h) => (
                  <label key={h.scheme_code} className="flex cursor-pointer items-center gap-2 text-xs">
                    <input type="checkbox" checked={form.mapped_scheme_codes.includes(h.scheme_code)} onChange={() => toggleScheme(h.scheme_code)} data-testid={`goal-map-${h.scheme_code}`} />
                    <span className="flex-1 truncate">{h.scheme_name}</span>
                    <span className="text-[var(--ink-muted)]">{formatCurrency(h.current_value)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <Button className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" disabled={saving || !form.name || !form.target_amount || !form.target_date} onClick={save} data-testid="goal-save-button">
            {saving ? "Saving..." : "Create goal"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProgressRing({ pct }) {
  const r = 34, c = 2 * Math.PI * r;
  return (
    <svg width="84" height="84" viewBox="0 0 84 84">
      <circle cx="42" cy="42" r={r} fill="none" stroke="var(--surface-2)" strokeWidth="8" />
      <circle cx="42" cy="42" r={r} fill="none" stroke="var(--cta)" strokeWidth="8" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - Math.min(100, pct) / 100)} transform="rotate(-90 42 42)" />
      <text x="42" y="47" textAnchor="middle" fontSize="16" fontWeight="700" fill="var(--brand)">{Math.round(pct)}%</text>
    </svg>
  );
}

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => Promise.all([
    clientApi.get("/goals").then((r) => setGoals(r.data)),
    clientApi.get("/portfolio/holdings").then((r) => setHoldings(r.data.holdings || [])),
  ]).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await clientApi.delete(`/goals/${id}`);
    toast.success("Goal deleted");
    load();
  };

  return (
    <ClientLayout>
      <div className="mb-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl text-[var(--brand)]">Goals</h1>
          <p className="mt-1 text-sm text-[var(--ink-muted)]">Give every rupee a purpose — track projections against your targets.</p>
        </div>
        <GoalDialog onSaved={load} holdings={holdings} />
      </div>
      {loading ? <div className="py-16 text-center text-sm text-[var(--ink-muted)]">Loading...</div> :
        goals.length === 0 ? (
          <Card className="glass-card max-w-xl border-0"><CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <Target className="h-10 w-10 text-[var(--cta)]" />
            <div className="font-heading text-lg text-[var(--brand)]" data-testid="no-goals-message">No goals yet</div>
            <p className="text-sm text-[var(--ink-muted)]">Create your first goal — retirement, education, a home — and we'll tell you if you're on track.</p>
          </CardContent></Card>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {goals.map((g) => {
              const c = g.computed || {};
              const Icon = (GOAL_TYPES.find(([k]) => k === g.goal_type) || GOAL_TYPES[6])[2];
              return (
                <Card key={g.id} className="glass-card border-0" data-testid={`goal-card-${g.id}`}>
                  <CardContent className="p-5">
                    <div className="flex flex-col gap-4 min-[380px]:flex-row min-[380px]:items-start">
                      <div className="self-center min-[380px]:self-auto"><ProgressRing pct={c.progress_pct || 0} /></div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 font-heading text-lg text-[var(--brand)]"><Icon className="h-4 w-4" /> {g.name}</div>
                          <button onClick={() => remove(g.id)} className="text-red-500 hover:text-red-700" data-testid={`goal-delete-${g.id}`}><Trash2 className="h-4 w-4" /></button>
                        </div>
                        <div className="mt-1 text-xs text-[var(--ink-muted)]">Target {formatCurrency(g.target_amount)} by {g.target_date} · {c.months_left} months left</div>
                        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                          <div className="rounded-lg bg-[var(--surface-2)] p-2">
                            <div className="text-[var(--ink-muted)]">Mapped value</div>
                            <div className="font-semibold">{formatCurrency(c.current_value)}</div>
                          </div>
                          <div className="rounded-lg bg-[var(--surface-2)] p-2">
                            <div className="text-[var(--ink-muted)]">Projected at target</div>
                            <div className="font-semibold">{formatCurrency(c.projected_value)}</div>
                          </div>
                        </div>
                        <div className={`mt-3 flex items-center gap-1.5 text-xs font-medium ${c.on_track ? "text-emerald-700" : "text-amber-600"}`} data-testid={`goal-track-${g.id}`}>
                          {c.on_track ? <><CheckCircle2 className="h-3.5 w-3.5" /> On track</> :
                            <><AlertCircle className="h-3.5 w-3.5" /> Off track — add {formatCurrency(c.additional_monthly_needed)}/month to catch up</>}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
    </ClientLayout>
  );
}
